import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';

import { useChatOverlayContextSafe } from '../components/chat-v2/contexts/chat-overlay-context';
import { appendPdfPageFragment, getFirstCitationPage, isPdfFilename } from '../components/chat-v2/lib/pdf-page-preview';
import { chatWorkspaceFileUrl } from '../lib/chat-workspace';

/**
 * Opens a file of the user's sandbox workspace in a new tab. The chat is the one the path belongs
 * to, or, for a schedule's inputs, the chat on screen.
 */
export function useOpenChatWorkspaceFile(): (options: { path: string; page?: string | null }) => void {
  const apollo = useApolloClient();
  const overlayChatUuid = useChatOverlayContextSafe()?.chatUuid;

  return useCallback(
    ({ path, page }) => {
      const chatUuid = /^\/mnt\/data\/chats\/([^/]+)\//.exec(path)?.[1] ?? overlayChatUuid;
      if (!chatUuid) {
        return;
      }
      // Opened synchronously so the popup isn't blocked; pointed at the signed link once it arrives.
      const tab = window.open('about:blank', '_blank');
      if (!tab) {
        return;
      }
      tab.opener = null;
      const pageNumber = isPdfFilename(path) ? getFirstCitationPage(page) : undefined;
      void chatWorkspaceFileUrl(apollo, { chatUuid, path })
        .then((url) => {
          // eslint-disable-next-line no-restricted-syntax -- Navigates a separately opened popup, never the embed or host window.
          tab.location.replace(pageNumber ? appendPdfPageFragment(url, pageNumber) : url);
        })
        .catch(() => tab.close());
    },
    [apollo, overlayChatUuid],
  );
}
